import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, LoadingSpinner, Button } from '@components/common';
import { BaseModal } from '@components/modals';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import { useLocationPermissions } from '@hooks/useLocationPermissions';
import { LocationTransactions } from './components';
import Icon from '@components/Icon/Icon';
import api from '@services/api';
import './LocationViewPage.scss';

const LocationViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, mode: 'delete' });

    // Permissions pour cette location
    const { permissions: locationPermissions, loading: permissionsLoading } = useLocationPermissions(id);

    useEffect(() => {
        const fetchLocation = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/locations/${id}`);
                setLocation(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch location:', err);
                setError(t('locationView.fetchError'));
                notify.error(t('locationView.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchLocation();
        }
    }, [id, t]); // Removed notify from dependencies to prevent infinite re-renders

    const handleBack = () => {
        navigate('/locations');
    };

    const handleEdit = () => {
        navigate(`/locations/${id}/edit`);
    };

    const handleDelete = () => {
        setModalState({ isOpen: true, mode: 'delete' });
    };

    const handleModalClose = () => {
        setModalState({ isOpen: false, mode: 'delete' });
    };

    const handleModalSuccess = () => {
        if (modalState.mode === 'delete') {
            navigate('/locations');
        }
    };

    if (loading) {
        return (
            <div className="location-view-page">
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('locationView.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !location) {
        return (
            <div className="location-view-page">
                <Card className="error-card">
                    <div className="error-content">
                        <h2>{t('locationView.errorTitle')}</h2>
                        <p>{error || t('locationView.notFound')}</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="location-view-page">
            <div className="location-details">
                <Card className="location-info-card">
                    <div className="location-timestamps">
                        <div className="timestamp">
                            <span className="label">{t('locationView.createdAt')}</span>
                            <span className="value">{new Date(location.created_at).toLocaleDateString()}</span>
                        </div>
                        {location.updated_at && (
                            <div className="timestamp">
                                <span className="label">{t('locationView.updatedAt')}</span>
                                <span className="value">{new Date(location.updated_at).toLocaleDateString()}</span>
                            </div>
                        )}
                    </div>

                    <div className="location-header">
                        <div className="location-icon">
                            <Icon icon="map-marker-alt" size="lg" />
                        </div>
                        <div className="location-info">
                            <h1>{location.name}</h1>
                            {location.description && (
                                <p className="location-description">{location.description}</p>
                            )}
                        </div>
                    </div>

                    <div className="location-metadata">
                        {location.address && (
                            <div className="metadata-item">
                                <span className="label">{t('locationView.address')}:</span>
                                <span className="value">{location.address}</span>
                            </div>
                        )}
                        {location.building && (
                            <div className="metadata-item">
                                <span className="label">{t('locationView.building')}:</span>
                                <span className="value">{location.building}</span>
                            </div>
                        )}
                        {location.floor && (
                            <div className="metadata-item">
                                <span className="label">{t('locationView.floor')}:</span>
                                <span className="value">{location.floor}</span>
                            </div>
                        )}
                        {location.room && (
                            <div className="metadata-item">
                                <span className="label">{t('locationView.room')}:</span>
                                <span className="value">{location.room}</span>
                            </div>
                        )}
                    </div>

                    {/* Edit and Delete buttons */}
                    <div className="location-actions">
                        {!permissionsLoading && locationPermissions.canUpdate && (
                            <Button
                                variant="primary"
                                onClick={handleEdit}
                            >
                                <span>{t('common.edit')}</span>
                            </Button>
                        )}

                        {!permissionsLoading && locationPermissions.canDelete && (
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                            >
                                <span>{t('common.delete')}</span>
                            </Button>
                        )}
                    </div>
                </Card>
            </div>

            <LocationTransactions
                locationId={id}
                className="location-transactions-section"
            />

            <BaseModal
                isOpen={modalState.isOpen}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
                mode={modalState.mode}
                item={location}
                itemType="location"
                deleteFn={async (id) => await api.delete(`/locations/${id}`)}
                size="small"
            />
        </div>
    );
};

export default LocationViewPage;
